import axios from 'axios';
import {
    MutationResultPair,
    QueryCache,
    QueryConfig,
    QueryKey,
    QueryResult,
    useMutation,
    useQuery,
    useQueryCache,
} from 'react-query';
import config from './config';
import { BaseEntite, BaseEntiteStatique, UtilisateurConnecte } from '../../../common/src/entities';

const api = axios.create({
    baseURL: config.API_URL,
    responseType: 'json',
});

export default api;

export const useGetQuery = <T>(key: string | (string | number | Object)[], config?: QueryConfig<T>): QueryResult<T> => {
    let url = '';

    if (Array.isArray(key)) {
        const lastElement = key[key.length - 1];
        const hasQueryParams = typeof lastElement === 'object' && lastElement !== null && !Array.isArray(lastElement);

        url = '/' + (hasQueryParams ? key.slice(0, -1).join('/') : key.join('/'));

        const params = hasQueryParams ? new URLSearchParams(lastElement as Record<string, string>).toString() : '';

        if (params) url += `?${params}`;
    } else {
        url = '/' + key;
    }

    return useQuery(
        key,
        async () => {
            const { data } = await api.get(url);
            return data;
        },
        config,
    );
};

export async function fetchAsync<T>(key: string | (string | number)[]): Promise<T> {
    const url = '/' + (Array.isArray(key) ? key.join('/') : key);
    try {
        const response = await api.get<T>(url);
        return response.data;
    } catch (error) {
        // Handle or throw the error based on your error handling strategy
        throw error;
    }
}

export type PostMutationResult<T> = MutationResultPair<T, unknown, T, () => void>;

export const usePostQuery = <T>(key: string, invalidPredicates?: QueryKey[]): PostMutationResult<T> => {
    const queryCache = useQueryCache();
    return useMutation(
        async (data: T) => {
            return (await api.post(`/${key}`, data)).data;
        },
        {
            onMutate: (created) => {
                queryCache.cancelQueries(key);
                const previousValues = queryCache.getQueryData<T[]>(key);
                if (previousValues) {
                    queryCache.setQueryData(key, [...previousValues, created]);
                }
                return () => queryCache.setQueryData(key, previousValues);
            },
            onError: (err, data, rollback) => rollback(),
            onSettled: () => {
                (invalidPredicates ?? [key]).forEach((invalidPredicate) => {
                    queryCache.invalidateQueries(invalidPredicate);
                });
            },
            throwOnError: true,
        },
    );
};

export type PatchMutationResult<T> = MutationResultPair<T, unknown, Partial<T>, () => void>;

function putPatchMutate<T extends Partial<BaseEntite>>(queryCache: QueryCache, key: string, data: T) {
    queryCache.cancelQueries(key);
    //update single query
    const previousValue = queryCache.getQueryData([key, data.id]);
    queryCache.setQueryData([key, data.id], data);
    //update list query
    const previousValues = queryCache.getQueryData<T[]>(key);
    if (previousValues) {
        const newValues = [...previousValues];
        const index = newValues.findIndex((x) => x.id == data.id);
        Object.assign(newValues[index], data);
        queryCache.setQueryData(key, newValues);
    }
    //return rollback function
    return () => {
        queryCache.setQueryData([key, data.id], previousValue);
        queryCache.setQueryData(key, previousValues);
    };
}

export const usePatchQuery = <T extends BaseEntite>(
    key: string,
    invalidPredicates?: QueryKey[],
): PatchMutationResult<T> => {
    const queryCache = useQueryCache();
    return useMutation(
        async (data: Partial<T>) => {
            return (await api.patch(`/${key}/${data.id}`, data)).data;
        },
        {
            onMutate: (data) => putPatchMutate(queryCache, key, data),
            onError: (err, data, rollback) => rollback(),
            onSuccess: (result) => queryCache.setQueryData([key, result.id], result),
            onSettled: () => {
                (invalidPredicates ?? [key]).forEach((invalidPredicate) => {
                    queryCache.invalidateQueries(invalidPredicate);
                });
            },
            throwOnError: true,
        },
    );
};

export type PutMutationResult<T> = MutationResultPair<T, unknown, T, () => void>;

export const usePutQuery = <T extends BaseEntite>(
    key: string,
    invalidPredicates?: QueryKey[],
): PutMutationResult<T> => {
    const queryCache = useQueryCache();
    return useMutation(
        async (data: T) => {
            return (await api.put(`/${key}/${data.id}`, data)).data;
        },
        {
            onMutate: (data) => putPatchMutate(queryCache, key, data),
            onError: (err, data, rollback) => rollback(),
            onSuccess: (result) => queryCache.setQueryData([key, result.id], result),
            onSettled: () => {
                (invalidPredicates ?? [key]).forEach((invalidPredicate) => {
                    queryCache.invalidateQueries(invalidPredicate);
                });
            },
            throwOnError: true,
        },
    );
};

export type DeleteMutationResult = MutationResultPair<void, unknown, number, () => void>;

export const useDeleteQuery = (key: string, invalidPredicates?: QueryKey[]): DeleteMutationResult => {
    const queryCache = useQueryCache();
    return useMutation(
        async (id) => {
            await api.delete(`/${key}/${id}`);
        },
        {
            onMutate: (id) => {
                queryCache.cancelQueries(key);
                const previousValues = queryCache.getQueryData<BaseEntite[]>(key);
                if (previousValues) {
                    const newValues = [...previousValues];
                    const index = newValues.findIndex((x) => x.id == id);
                    newValues.splice(index, 1);
                    queryCache.setQueryData(key, newValues);
                }
                return () => queryCache.setQueryData(key, previousValues);
            },
            onError: (err, data, rollback) => rollback(),
            onSettled: () => {
                (invalidPredicates ?? [key]).forEach((invalidPredicate) => {
                    queryCache.invalidateQueries(invalidPredicate);
                });
            },
            throwOnError: true,
        },
    );
};

export async function sessionOuverte(): Promise<UtilisateurConnecte | undefined> {
    try {
        const { data } = await api.get('/authentification/connecte');
        return data;
    } catch (e) {
        console.log(e);
    }
    return undefined;
}

export async function fermerSession(): Promise<void> {
    try {
        await api.post('/authentification/deconnexion');
    } catch (error) {
        console.log(error);
    }
}

export const useEntites = <T extends BaseEntite>(entite: BaseEntiteStatique<T>): QueryResult<T[]> =>
    useGetQuery(entite.key);
export const useEntite = <T extends BaseEntite>(entite: BaseEntiteStatique<T>, id: number): QueryResult<T> =>
    useGetQuery([entite.key, id]);
export const useCreateEntite = <T extends BaseEntite>(entite: BaseEntiteStatique<T>): PostMutationResult<T> =>
    usePostQuery(entite.key);
export const useUpdateEntite = <T extends BaseEntite>(entite: BaseEntiteStatique<T>): PatchMutationResult<T> =>
    usePatchQuery(entite.key);
export const useReplaceEntite = <T extends BaseEntite>(entite: BaseEntiteStatique<T>): PutMutationResult<T> =>
    usePutQuery(entite.key);
export const useDeleteEntite = <T extends BaseEntite>(entite: BaseEntiteStatique<T>): DeleteMutationResult =>
    useDeleteQuery(entite.key);
